import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Chip,
  Button,
  Collapse,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import {
  getIngredientCategoryColor,
  getProgressColor,
  getGreyBackground,
} from "../theme/ClientTheme";

/**
 * Shopping list component showing consolidated ingredients by category
 * Includes checkboxes for marking items as purchased
 * Uses smart shopping list API for grocery store rounded quantities
 */
const ClientShoppingList = ({
  shoppingList,
  mealPlan,
  organizationId,
  mealPlanId,
  isLoading = false,
  groupedByCategory,
}) => {
  const theme = useTheme();
  // Create a unique key for localStorage based on organization and meal plan
  const storageKey = `shopping-list-${organizationId}-${mealPlanId}`;

  // Load initial checked state from localStorage
  const loadCheckedItems = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (error) {
      console.warn("Failed to load shopping list state:", error);
      return new Set();
    }
  };

  const [checkedItems, setCheckedItems] = useState(loadCheckedItems);
  const [expandedCategories, setExpandedCategories] = useState(
    new Set(["Proteins", "Vegetables", "Fruits"]),
  );

  // Use groupedByCategory from API if available, otherwise group locally
  const ingredientsByCategory = React.useMemo(() => {
    if (groupedByCategory) {
      return groupedByCategory;
    }
    
    // Fallback: group locally
    return shoppingList.reduce((acc, ingredient) => {
      const category = ingredient.category || "Other";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(ingredient);
      return acc;
    }, {});
  }, [groupedByCategory, shoppingList]);

  const categories = Object.keys(ingredientsByCategory).sort();

  // Save checked items to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...checkedItems]));
    } catch (error) {
      console.warn("Failed to save shopping list state:", error);
    }
  }, [checkedItems, storageKey]);

  const handleItemCheck = (ingredientName) => {
    const newCheckedItems = new Set(checkedItems);
    if (newCheckedItems.has(ingredientName)) {
      newCheckedItems.delete(ingredientName);
    } else {
      newCheckedItems.add(ingredientName);
    }
    setCheckedItems(newCheckedItems);
  };

  const handleCategoryToggle = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleResetAll = () => {
    setCheckedItems(new Set());
  };

  const formatQuantity = (ingredient) => {
    const quantity = ingredient.amount; // Fixed: use 'amount' instead of 'totalQuantity'
    const unit = ingredient.unit;

    // Round to reasonable precision
    if (quantity < 1) {
      return `${Math.round(quantity * 100) / 100} ${unit}`;
    } else if (quantity < 10) {
      return `${Math.round(quantity * 10) / 10} ${unit}`;
    } else {
      return `${Math.round(quantity)} ${unit}`;
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Proteins: "🥩",
      Vegetables: "🥬",
      Fruits: "🍎",
      Grains: "🌾",
      Dairy: "🥛",
      Pantry: "🧂",
      Other: "🛒",
    };
    return icons[category] || "📦";
  };

  const getCategoryColor = (category) => {
    return getIngredientCategoryColor(category, theme);
  };

  const totalItems = shoppingList.length;
  const checkedCount = checkedItems.size;
  const progressPercentage =
    totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            Loading smart shopping list...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h3" sx={{ fontWeight: 600 }}>
          Shopping List
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<ClearIcon />}
            variant="outlined"
            onClick={handleResetAll}
            sx={{
              textTransform: "none",
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0, sm: 0.5 },
              },
            }}
            color="error"
          >
            <Box sx={{ display: { xs: "none", sm: "block" } }}>Reset All</Box>
          </Button>
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            onClick={handlePrint}
            sx={{
              textTransform: "none",
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0, sm: 0.5 },
              },
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "block" } }}>Print List</Box>
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            sx={{
              textTransform: "none",
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0, sm: 0.5 },
              },
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              Download PDF
            </Box>
          </Button>
        </Stack>
      </Stack>

      {/* Progress summary */}
      <Card
        sx={{ mb: 4, bgcolor: "success.light", color: "success.contrastText" }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Shopping Progress
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {checkedCount} of {totalItems} items collected
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {progressPercentage}%
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Complete
              </Typography>
            </Box>
          </Stack>

          {/* Progress bar */}
          <Box
            sx={{
              mt: 2,
              bgcolor: "rgba(255,255,255,0.3)",
              borderRadius: 1,
              height: 8,
            }}
          >
            <Box
              sx={{
                bgcolor: getProgressColor(theme),
                height: "100%",
                borderRadius: 1,
                width: `${progressPercentage}%`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Meal plan info */}
      <Card sx={{ mb: 4, bgcolor: getGreyBackground(theme) }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            📋 {mealPlan.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This smart shopping list includes all ingredients needed for the complete
            7-day meal plan. Quantities are rounded to grocery store package sizes
            (e.g., eggs to dozens, milk to gallons/quarts) and include all days of the meal plan.
          </Typography>
        </CardContent>
      </Card>

      {/* Shopping list by category */}
      <Grid container spacing={3}>
        {categories.map((category) => {
          const items = ingredientsByCategory[category];
          const isExpanded = expandedCategories.has(category);
          const categoryChecked = items.filter((item) =>
            checkedItems.has(item.name),
          ).length;

          return (
            <Grid item xs={12} md={6} lg={4} key={category}>
              <Card sx={{ height: "fit-content" }}>
                {/* Category header */}
                <CardContent sx={{ pb: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleCategoryToggle(category)}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="h4" sx={{ fontSize: "1.5rem" }}>
                        {getCategoryIcon(category)}
                      </Typography>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {category}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {categoryChecked}/{items.length} items
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton>
                      <ExpandMoreIcon
                        sx={{
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    </IconButton>
                  </Stack>

                  {/* Category progress bar */}
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Box
                      sx={{ bgcolor: "grey.200", borderRadius: 1, height: 4 }}
                    >
                      <Box
                        sx={{
                          bgcolor: getCategoryColor(category),
                          height: "100%",
                          borderRadius: 1,
                          width: `${items.length > 0 ? (categoryChecked / items.length) * 100 : 0}%`,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>

                {/* Category items */}
                <Collapse in={isExpanded}>
                  <CardContent sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {items.map((ingredient) => {
                        const isChecked = checkedItems.has(ingredient.name);

                        return (
                          <FormControlLabel
                            key={ingredient.name}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() =>
                                  handleItemCheck(ingredient.name)
                                }
                                size="small"
                                sx={{
                                  color: getCategoryColor(category),
                                  "&.Mui-checked": {
                                    color: getCategoryColor(category),
                                  },
                                }}
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  width: "100%",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    flexGrow: 1,
                                    textDecoration: isChecked
                                      ? "line-through"
                                      : "none",
                                    opacity: isChecked ? 0.6 : 1,
                                    fontWeight: 500,
                                  }}
                                >
                                  {ingredient.name}
                                </Typography>
                                <Chip
                                  label={formatQuantity(ingredient)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: "0.75rem",
                                    height: 20,
                                    opacity: isChecked ? 0.6 : 1,
                                  }}
                                />
                              </Box>
                            }
                            sx={{
                              m: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "100%",
                              },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Collapse>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Shopping tips */}
      <Card sx={{ mt: 4, bgcolor: "info.light", color: "info.contrastText" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            💡 Shopping Tips
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                • Shop the perimeter first (fresh items)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                • Check what you already have at home
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                • Consider buying in bulk for pantry items
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                • Bring a cooler bag for frozen/refrigerated items
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                • Check expiration dates, especially for produce
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                • Consider substitutions for seasonal items
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientShoppingList;
